/**
 * The reply audio: PCM int16 LE, 24 kHz, mono, one binary frame per clause, played gapless with
 * Web Audio. It owns the content clock the avatar draws against and the gate that holds the first
 * sound until the avatar's first frame of the reply is ready (docs/NEW-UI/protocollo.md §6–7).
 *
 * Same behaviour as the old kiosk (backend/frontend/src/hooks/voice/playbackScheduler.ts,
 * scheduleAudio.ts), gathered in one owner instead of a bundle of refs. The timing math and the
 * gate are the tested pure functions of ../avatar/sync.ts.
 */
import {
  AudioGate, GATE_MAX_MS, avDebugEnabled, computePlayedMs, getAvsyncPersistent, getAvsyncStats, resetAvsyncStats,
  scheduleChunk, shouldStopPreviousEpoch, type GateReason,
} from '../avatar/sync';
import type { PlaybackClock, SpokenClause } from './controller';
import { pcm16 } from '../avatar/wav';
import {
  isAudioEndShort, isClauseShortfall, recordAudioEndShort, recordClauseShortfall, recordCtxHealth, recordGateReason,
  recordPlaybackStop, type PlaybackStopCause,
} from '../avatar/sync-telemetry';

export const PLAYBACK_RATE = 24000;

/** How long `bytes` of reply PCM (16-bit mono at PLAYBACK_RATE) sound, in ms. */
export function pcmMs(bytes: number): number {
  return (bytes / (PLAYBACK_RATE * 2)) * 1000;
}

export interface PlaybackEvents {
  /** A reply started / ended playing: the backend tracks it for barge-in (`playback_started/_ended`). */
  started(): void;
  ended(): void;
  /** The whole reply has been heard. */
  drained(): void;
  /** Is a face that holds the audio for its first frame there (a MuseTalk viewer, a face reading the audio)? Without one the gate never holds it. */
  avatarConnected(): boolean;
  /** A clause of a reply is scheduled, or held behind the gate (not a filler's: it has no reply clock). */
  spoken(clause: SpokenClause): void;
}

/** At the reply's own rate when the browser allows it; else at the device's, which resamples each 24 kHz buffer. */
function openContext(): AudioContext {
  try {
    return new AudioContext({ sampleRate: PLAYBACK_RATE });
  } catch {
    return new AudioContext();
  }
}

export class ReplyPlayback {
  private context: AudioContext | null = null;
  private queue: AudioBufferSourceNode[] = [];
  private gate = new AudioGate<ArrayBuffer>();
  private gateTimer: ReturnType<typeof setTimeout> | null = null;
  private audioEndReceived = false;
  private serverBytes: number | null = null;
  private utteranceStart: number | null = null;
  private nextStart = 0;
  private scheduledMs = 0;
  private gapMs = 0;
  /** The content held behind the gate so far: where the next held clause starts. */
  private heldMs = 0;
  /** Clauses already given to the faces while held: not given again when scheduled. */
  private readonly announced = new WeakSet<ArrayBuffer>();
  private clauseIdx = 0;
  /** Bumped by every forced stop, so a source ending after one is not counted as a short clause. */
  private stopGen = 0;

  constructor(private readonly events: PlaybackEvents) {}

  /** Create or resume the audio context. Call it from a user gesture (autoplay rules). */
  unlock(): AudioContext {
    if (!this.context) this.context = openContext();
    if (this.context.state === 'suspended') this.context.resume().catch(() => undefined); // a later gesture retries
    return this.context;
  }

  /** `assistant_audio_start {avatar_epoch}`: a new reply. */
  beginTurn(epoch: number | null): void {
    const previous = this.gate.epoch;
    if (shouldStopPreviousEpoch(previous, epoch, this.queue.length)) {
      this.stopSources('overlap'); // two replies must never sound at once
      const p = getAvsyncPersistent();
      if (p) p.overlapStops += 1;
      if (avDebugEnabled()) console.debug(`[avdbg] epoch-overlap stop prev=${previous} new=${epoch}`);
    }
    this.resetClock();
    resetAvsyncStats(epoch).gate.armTs = performance.now();
    this.gate.arm(epoch, this.events.avatarConnected());
    this.events.started();
  }

  /** A binary frame: PCM of the current reply (or of a filler, outside a reply). */
  push(pcm: ArrayBuffer): void {
    const released = this.gate.push(pcm);
    if (!this.gate.open && this.gate.epoch !== null) {
      // held: a face that reads the audio gets it now, and opens the gate once its first mouths are read
      this.events.spoken({ turn: this.gate.epoch, atMs: this.heldMs, pcm });
      this.announced.add(pcm);
      this.heldMs += pcmMs(pcm.byteLength);
    }
    if (!this.gate.open && this.gateTimer === null) {
      this.gateTimer = setTimeout(() => this.openGate('timeout'), GATE_MAX_MS);
      const stats = getAvsyncStats();
      if (stats && stats.gate.firstPcmTs === null) stats.gate.firstPcmTs = performance.now();
    }
    for (const chunk of released) this.schedule(chunk);
  }

  /** `assistant_audio_end {total_bytes}`. */
  endTurn(totalBytes: number | undefined): void {
    this.audioEndReceived = true;
    if (typeof totalBytes === 'number') this.serverBytes = totalBytes;
    if (!this.gate.open) {
      if (this.gate.bufferedCount === 0 && this.gateTimer === null) this.finish(); // an empty reply
    } else if (this.queue.length === 0) {
      this.finish();
    }
  }

  /** The avatar has its first frame of `turn`: let the held audio start. */
  firstFrame(turn: number): void {
    if (this.gate.matchesTurn(turn)) this.openGate('first-frame');
  }

  /** Barge-in or the stop button: silence now. */
  stop(cause: PlaybackStopCause): void {
    this.stopSources(cause);
    this.resetClock();
    this.gate.reset();
    this.events.ended();
  }

  /** How much of the current reply has been heard, for the avatar (null when not speaking). */
  clock(): PlaybackClock | null {
    const epoch = this.gate.epoch;
    const ctx = this.context;
    if (epoch === null || this.utteranceStart === null || !ctx) return null;
    // Audible time: the write cursor plus the output latency the platform reports.
    const latency = ctx.outputLatency || ctx.baseLatency || 0;
    const wallMs = (ctx.currentTime - this.utteranceStart - latency) * 1000;
    const totalMs = this.serverBytes === null ? null : pcmMs(this.serverBytes);
    return { turn: epoch, playedMs: computePlayedMs(wallMs, this.gapMs, this.scheduledMs), totalMs };
  }

  dispose(): void {
    this.stopSources('barge');
    this.resetClock();
    this.gate.reset();
    void this.context?.close();
    this.context = null;
  }

  private openGate(reason: GateReason): void {
    if (this.gateTimer !== null) clearTimeout(this.gateTimer);
    this.gateTimer = null;
    const stats = getAvsyncStats();
    if (stats && stats.gate.openTs === null) {
      stats.gate.openTs = performance.now();
      stats.gate.reason = reason;
      recordGateReason(stats.persistent, stats.turn, reason);
      if (avDebugEnabled()) console.debug(`[avdbg] gate open turn=${stats.turn} reason=${reason}`);
    }
    for (const chunk of this.gate.release()) this.schedule(chunk);
  }

  private schedule(pcm: ArrayBuffer): void {
    const ctx = this.unlock();
    const samples = pcm.byteLength / 2;
    if (samples === 0) return;
    const firstOfTurn = this.utteranceStart === null;
    if (firstOfTurn) {
      const p = getAvsyncPersistent();
      if (p) recordCtxHealth(p, { turn: this.gate.epoch, ctxState: ctx.state, currentTime: ctx.currentTime, outputLatency: ctx.outputLatency || 0 });
    }
    const buffer = ctx.createBuffer(1, samples, PLAYBACK_RATE);
    buffer.copyToChannel(pcm16(pcm), 0);

    const next = scheduleChunk(
      { utteranceStartSec: this.utteranceStart, gapMs: this.gapMs, scheduledContentMs: this.scheduledMs, nextPlaybackSec: this.nextStart },
      ctx.currentTime,
      buffer.duration,
    );
    if (this.gate.epoch !== null && !this.announced.has(pcm)) this.events.spoken({ turn: this.gate.epoch, atMs: this.scheduledMs, pcm });
    const underrunMs = next.state.gapMs - this.gapMs;
    this.utteranceStart = next.state.utteranceStartSec;
    this.gapMs = next.state.gapMs;
    this.scheduledMs = next.state.scheduledContentMs;
    this.nextStart = next.state.nextPlaybackSec;
    const stats = getAvsyncStats();
    if (stats) stats.lastContentMs = this.scheduledMs;
    if (underrunMs > 0) {
      const p = getAvsyncPersistent();
      if (p) p.scheduleGaps += 1;
      if (avDebugEnabled()) console.debug(`[avdbg] audio underrun gap=${Math.round(underrunMs)}ms`);
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(next.startSec);
    this.clauseIdx = firstOfTurn ? 0 : this.clauseIdx + 1;
    const clause = { idx: this.clauseIdx, turn: this.gate.epoch, endSec: next.startSec + buffer.duration, gen: this.stopGen };
    this.queue.push(source);
    source.onended = () => {
      this.queue = this.queue.filter((s) => s !== source);
      const now = this.context;
      // A clause that ended early on a running clock, not cut by us: record it (field diagnosis).
      if (now && now.state === 'running' && this.stopGen === clause.gen && isClauseShortfall(clause.endSec, now.currentTime)) {
        const p = getAvsyncPersistent();
        if (p) {
          const expectedMs = buffer.duration * 1000;
          const shortfallMs = (clause.endSec - now.currentTime) * 1000;
          recordClauseShortfall(p, { turn: clause.turn, clauseIdx: clause.idx, expectedMs, actualMs: Math.max(0, expectedMs - shortfallMs), shortfallMs, wall: performance.now() });
        }
      }
      if (this.audioEndReceived && this.queue.length === 0) this.finish();
    };
  }

  private finish(): void {
    if (this.serverBytes !== null) {
      const p = getAvsyncPersistent();
      const serverMs = pcmMs(this.serverBytes);
      if (p && isAudioEndShort(this.scheduledMs, serverMs)) recordAudioEndShort(p, { turn: this.gate.epoch, scheduledMs: this.scheduledMs, serverMs });
    }
    this.resetClock();
    this.gate.reset();
    this.events.drained();
    this.events.ended();
  }

  private stopSources(cause: PlaybackStopCause): void {
    this.stopGen += 1;
    const p = getAvsyncPersistent();
    if (p) {
      const ctx = this.context;
      recordPlaybackStop(p, {
        turn: this.gate.epoch,
        cause,
        playedMs: ctx && this.utteranceStart !== null ? (ctx.currentTime - this.utteranceStart) * 1000 : 0,
        scheduledContentMs: this.scheduledMs,
        wall: performance.now(),
      });
    }
    for (const source of this.queue) {
      try {
        source.stop();
      } catch {
        // already ended: nothing to stop
      }
    }
    this.queue = [];
  }

  private resetClock(): void {
    this.audioEndReceived = false;
    this.serverBytes = null;
    this.utteranceStart = null;
    this.scheduledMs = 0;
    this.gapMs = 0;
    this.heldMs = 0;
    this.nextStart = 0;
    if (this.gateTimer !== null) clearTimeout(this.gateTimer);
    this.gateTimer = null;
  }
}
